import React, { useEffect, useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2, Car, Clock, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../lib/i18n';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    deliveryJob,
    driverInfo,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const [searchingMinutes, setSearchingMinutes] = useState(0);
  const {
    t
  } = useLanguage();
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Safari / WhatsApp fallback using execCommand
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is blocked
      setCopied(false);
    }
  };
  // Calculate searching time
  useEffect(() => {
    if (order?.status === 'searching_driver' && deliveryJob?.created_at) {
      const interval = setInterval(() => {
        const diff = Math.floor((new Date().getTime() - new Date(deliveryJob.created_at).getTime()) / 60000);
        setSearchingMinutes(diff > 0 ? diff : 0);
      }, 10000);
      // Initial calculation
      const diff = Math.floor((new Date().getTime() - new Date(deliveryJob.created_at).getTime()) / 60000);
      setSearchingMinutes(diff > 0 ? diff : 0);
      return () => clearInterval(interval);
    }
  }, [order?.status, deliveryJob?.created_at]);
  if (loading) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6427">
        <MarketplaceHeader data-id="element-6428" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-6429">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-6430" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-6431">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-6432" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-6433" />
            <Skeleton className="h-4 w-2/3" data-id="element-6434" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-6435">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-6436">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-6437" />
                <div className="flex-1" data-id="element-6438">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-6439" />
                  <Skeleton className="h-4 w-full" data-id="element-6440" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-6441" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6442">
        <MarketplaceHeader data-id="element-6443" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-6444">
          <EmptyState icon={Search} title={t('tracking_not_found')} description={t('error')} actionLabel={t('confirm_back')} onAction={() => window.location.href = '/'} data-id="element-6445" />
        </main>
        <Footer data-id="element-6446" />
      </div>;
  }
  const trackingUrl = window.location.href;
  const statusLabel = t(`status_${order.status}`) || getStatusLabel(order.status);
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6447">
      <MarketplaceHeader data-id="element-6448" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12" data-id="element-6449">
        <div className="flex items-center justify-between mb-8" data-id="element-6450">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-6451">
            {t('tracking_title')}
          </h1>
          <Badge variant="secondary" className={`text-sm px-3 py-1 ${getStatusColor(order.status)}`} data-id="element-6452">
            {statusLabel}
          </Badge>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-6453">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100" data-id="element-6454">
            <div data-id="element-6455">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-6456">
                {t('confirm_order_number')}
              </p>
              <p className="text-2xl font-black text-gray-900" data-id="element-6457">
                #{order.order_number}
              </p>
            </div>
            <div className="text-right" data-id="element-6458">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-6459">
                {t('total')}
              </p>
              <p className="text-2xl font-black text-brand-primary" data-id="element-6460">
                {formatCurrency(order.total)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6461">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-6462">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-6463">
                <Store className="w-5 h-5 text-blue-600" data-id="element-6464" />
              </div>
              <div data-id="element-6465">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-6466">
                  Restaurante
                </h3>
                <p className="font-medium text-gray-900" data-id="element-6467">
                  Pedido del restaurante
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-6468">
                  {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-6469">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-6470">
                <Receipt className="w-5 h-5 text-emerald-600" data-id="element-6471" />
              </div>
              <div data-id="element-6472">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-6473">
                  {t('total')}
                </h3>
                <p className="font-bold text-xl text-gray-900" data-id="element-6474">
                  {formatCurrency(order.total)}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-6475">
                  Pago:{' '}
                  {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Driver Status Card (Only for delivery orders in specific states) */}
        {order.delivery_type === 'delivery' && <>
            {order.status === 'searching_driver' && <div className="bg-orange-50 rounded-2xl border border-orange-200 shadow-sm p-6 mb-8 flex items-center gap-4" data-id="element-6476">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center shrink-0 animate-pulse" data-id="element-6477">
                  <Search className="w-6 h-6 text-orange-600" data-id="element-6478" />
                </div>
                <div data-id="element-6479">
                  <h3 className="font-bold text-orange-900 text-lg" data-id="element-6480">
                    Buscando un conductor para tu pedido...
                  </h3>
                  <p className="text-orange-700 font-medium flex items-center gap-1.5 mt-1" data-id="element-6481">
                    <Clock className="w-4 h-4" data-id="element-6482" />
                    Lleva {searchingMinutes}{' '}
                    {searchingMinutes === 1 ? 'minuto' : 'minutos'} buscando
                  </p>
                </div>
              </div>}

            {driverInfo && (order.status === 'ready' || order.status === 'out_for_delivery') && <div className="bg-cyan-50 rounded-2xl border border-cyan-200 shadow-sm p-6 mb-8 flex items-center gap-4" data-id="element-6483">
                  <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center shrink-0" data-id="element-6484">
                    <Car className="w-6 h-6 text-cyan-600" data-id="element-6485" />
                  </div>
                  <div className="flex-1" data-id="element-6486">
                    <h3 className="font-bold text-cyan-900 text-lg" data-id="element-6487">
                      {order.status === 'out_for_delivery' ? 'Tu pedido está en camino' : 'Tu conductor está en camino al restaurante'}
                    </h3>
                    <div className="mt-2 bg-white/60 rounded-xl p-3 border border-cyan-100" data-id="element-6488">
                      <p className="font-bold text-gray-900" data-id="element-6489">
                        {driverInfo.name}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-6490">
                        {driverInfo.vehicleType} • {driverInfo.plate}
                      </p>
                    </div>
                  </div>
                </div>}
          </>}

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-6491">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-6492">
            {t('tracking_title')}
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} restaurantId={order.restaurant_id} deliveryType={order.delivery_type} data-id="element-6493" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-6494">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6495">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-6496" />
              {t('checkout_address')}
            </h3>
            <p className="text-gray-600" data-id="element-6497">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-6498">
                <span className="font-semibold" data-id="element-6499">Notas:</span> {order.notes}
              </div>}
          </div>}

        {/* Tracking Link */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-6500">
          <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-6501">
            {t('copy_tracking')}
          </p>
          <div className="flex items-center gap-3" data-id="element-6502">
            <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-6503">
              {trackingUrl}
            </div>
            <button onClick={handleCopyLink} className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl border-2 border-gray-200 hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-6504">
              {copied ? <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-6505" /> : <Copy className="w-5 h-5" data-id="element-6506" />}
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-6507" />
    </div>;
}